// SPDX-License-Identifier: AGPL-3.0-or-later

import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {User} from '@app/features/user/models/User';
import {LRUMap} from '@app/lib/list/ListLruMap';
import type {TableGameLeague, TableGameRankedKind} from '@fluxer/constants/src/TableGameConstants';
import type {
	GuildTableGameLeaderboardEntry,
	GuildTableGameLeaderboardResponse,
	MyTableGameRatingsResponse,
	TableGameLeaderboardEntry,
	TableGameLeaderboardResponse,
	TableGameRatingSummary,
} from '@fluxer/schema/src/domains/table_game/TableGameLeaderboardSchemas';
import type {TableGameRewardShowcaseResponse} from '@fluxer/schema/src/domains/table_game/TableGameRewardSchemas';
import {makeAutoObservable, observableRef, observableShallow} from 'mobx';

export type LeaderboardPeriod = 'current' | 'previous';
export type LeaderboardScope =
	| {kind: 'global'; game: TableGameRankedKind; period: LeaderboardPeriod}
	| {kind: 'guild'; guildId: string; period: LeaderboardPeriod};
export type LeaderboardFailure = 'disabled' | 'forbidden' | 'error';

export const LEADERBOARD_PAGE_SIZE = 50;
// The server serves the first 1000 places of any board; a modal holds at most eight boards of them.
export const LEADERBOARD_MAX_ROWS = 1000;
export const LEADERBOARD_MAX_BOARDS = 8;
export const LEADERBOARD_SUMMARY_ROWS = 3;
export const LEADERBOARD_MAX_WEEK_RANKS = 50;

export interface LeaderboardRow {
	readonly rank: number;
	readonly userId: string;
	// A row's own copy of the user; board users are never written to the global Users store.
	readonly user: User | null;
	readonly league: TableGameLeague | null;
	readonly progress: number | null;
	readonly rr: number | null;
	readonly points: number | null;
	readonly games: number | null;
	readonly wins: number | null;
}

export interface LeaderboardBoard {
	readonly scope: LeaderboardScope;
	readonly rows: ReadonlyArray<LeaderboardRow>;
	readonly total: number;
	readonly hasMore: boolean;
	readonly isLoading: boolean;
	readonly failure: LeaderboardFailure | null;
	readonly me: GuildTableGameLeaderboardResponse['me'];
	// The season of a global board as YYYYMM, from its first answer; null before it and on a weekly board.
	readonly season: number | null;
}

// The feed's leaderboard card: my ratings and the head of one board, loaded once per signed-in session.
export interface LeaderboardSummary {
	readonly ratings: MyTableGameRatingsResponse | null;
	readonly game: TableGameRankedKind;
	readonly top: ReadonlyArray<LeaderboardRow>;
}

export function leaderboardKey(scope: LeaderboardScope): string {
	return scope.kind === 'global' ? `g:${scope.game}:${scope.period}` : `w:${scope.guildId}:${scope.period}`;
}

function toRow(entry: TableGameLeaderboardEntry | GuildTableGameLeaderboardEntry): LeaderboardRow {
	const global = 'league' in entry;
	return {
		rank: entry.rank,
		userId: entry.user_id,
		user: entry.user ? new User(entry.user) : null,
		league: global ? entry.league : null,
		progress: global ? entry.progress : null,
		rr: global ? entry.rr : null,
		points: global ? null : entry.points,
		games: global ? null : entry.games,
		wins: global ? null : entry.wins,
	};
}

class TableGameLeaderboards {
	boards = new Map<string, LeaderboardBoard>();
	myRatings: MyTableGameRatingsResponse | null = null;
	showcase: TableGameRewardShowcaseResponse | null = null;
	// Bumped by release(): an answer to a request made before it is dropped.
	generation = 0;
	// Session state outside the modal's ownership, dropped on sign-out or account switch (resetSession).
	summary: LeaderboardSummary | null = null;
	guildWeekRanks = new Map<string, {readonly rank: number; readonly endsAt: number}>();
	// Bumped by resetSession(): an answer to a request made in an earlier session is dropped.
	session = 0;
	private order = new LRUMap<string, true>(LEADERBOARD_MAX_BOARDS);
	private summaryClaimed = false;
	private showcaseClaimed = false;
	private owners = 0;

	constructor() {
		makeAutoObservable<this, 'order' | 'summaryClaimed' | 'showcaseClaimed' | 'owners'>(
			this,
			{
				boards: observableShallow,
				myRatings: observableRef,
				showcase: observableRef,
				summary: observableRef,
				guildWeekRanks: observableShallow,
				order: false,
				summaryClaimed: false,
				showcaseClaimed: false,
				owners: false,
			},
			{autoBind: true},
		);
	}

	getBoard(key: string): LeaderboardBoard | null {
		return this.boards.get(key) ?? null;
	}

	// Read while drawing, no timer: once the week has ended the place is gone on the next render.
	getGuildWeekRank(guildId: string): number | null {
		const place = this.guildWeekRanks.get(guildId);
		return place && Date.now() < place.endsAt ? place.rank : null;
	}

	getMyRating(game: TableGameRankedKind): TableGameRatingSummary | null {
		return this.myRatings?.ratings.find((rating) => rating.game === game) ?? null;
	}

	ensureBoard(scope: LeaderboardScope): string {
		const key = leaderboardKey(scope);
		if (!this.order.has(key) && this.order.size >= LEADERBOARD_MAX_BOARDS) {
			const oldest = this.order.keys().next().value;
			if (oldest !== undefined) this.boards.delete(oldest);
		}
		this.order.set(key, true);
		if (!this.boards.has(key)) {
			this.boards.set(key, {
				scope,
				rows: [],
				total: 0,
				hasMore: true,
				isLoading: false,
				failure: null,
				me: null,
				season: null,
			});
		}
		return key;
	}

	handlePagePending(key: string): void {
		this.patch(key, {isLoading: true, failure: null});
	}

	handlePage(
		key: string,
		generation: number,
		offset: number,
		page: TableGameLeaderboardResponse | GuildTableGameLeaderboardResponse,
	): void {
		const board = this.boards.get(key);
		if (generation !== this.generation || !board || offset !== board.rows.length) return;
		// Places can shift between two pages; a user already listed keeps the first place seen.
		const seen = new Set(board.rows.map((row) => row.userId));
		const added = page.entries.filter((entry) => !seen.has(entry.user_id)).map(toRow);
		const rows = [...board.rows, ...added].slice(0, LEADERBOARD_MAX_ROWS);
		this.patch(key, {
			rows,
			total: page.total,
			hasMore: page.entries.length >= LEADERBOARD_PAGE_SIZE && rows.length < Math.min(page.total, LEADERBOARD_MAX_ROWS),
			isLoading: false,
			me: 'me' in page ? page.me : board.me,
			season: 'season' in page ? page.season : board.season,
		});
	}

	handlePageFailure(key: string, generation: number, failure: LeaderboardFailure): void {
		if (generation !== this.generation) return;
		this.patch(key, {isLoading: false, failure, hasMore: failure === 'error'});
	}

	handleRetry(key: string): void {
		this.patch(key, {failure: null, hasMore: true});
	}

	handleMyRatings(generation: number, ratings: MyTableGameRatingsResponse): void {
		if (generation === this.generation) this.myRatings = ratings;
	}

	// One read per ownership lifetime, including failed reads; release permits a fresh catalogue.
	claimShowcase(): number | null {
		if (this.showcaseClaimed) return null;
		this.showcaseClaimed = true;
		return this.generation;
	}

	handleShowcase(generation: number, body: TableGameRewardShowcaseResponse | null): void {
		if (generation === this.generation) this.showcase = body;
	}

	// One summary load per session: the first caller gets the session to answer with, every later one null until a
	// failed load gives the claim back (handleSummary).
	claimSummary(): number | null {
		if (this.summaryClaimed) return null;
		this.summaryClaimed = true;
		return this.session;
	}

	handleSummary(
		session: number,
		ratings: MyTableGameRatingsResponse | null,
		game: TableGameRankedKind,
		entries: ReadonlyArray<TableGameLeaderboardEntry>,
		failed: boolean,
	): void {
		if (session !== this.session) return;
		this.summary = {ratings, game, top: entries.slice(0, LEADERBOARD_SUMMARY_ROWS).map(toRow)};
		if (failed) this.summaryClaimed = false;
	}

	// My place on a community's board this week, seen while it was open; a bounded map, the oldest goes first.
	rememberGuildWeekRank(session: number, guildId: string, rank: number | null, endsAt: string): void {
		if (session !== this.session) return;
		this.guildWeekRanks.delete(guildId);
		if (rank === null) return;
		if (this.guildWeekRanks.size >= LEADERBOARD_MAX_WEEK_RANKS) {
			const oldest = this.guildWeekRanks.keys().next().value;
			if (oldest !== undefined) this.guildWeekRanks.delete(oldest);
		}
		this.guildWeekRanks.set(guildId, {rank, endsAt: Date.parse(endsAt)});
	}

	resetSession(): void {
		this.session += 1;
		this.summaryClaimed = false;
		this.summary = null;
		this.guildWeekRanks.clear();
	}

	// The page and the modal own the boards together: the last one to let go drops them (release).
	acquire(): () => void {
		this.owners += 1;
		let held = true;
		return () => {
			if (!held) return;
			held = false;
			this.owners -= 1;
			if (this.owners === 0) this.release();
		};
	}

	// Drops the boards, my ratings and any answer still on its way.
	release(): void {
		this.generation += 1;
		this.boards.clear();
		this.order.clear();
		this.myRatings = null;
		this.showcase = null;
		this.showcaseClaimed = false;
	}

	private patch(key: string, changes: Partial<LeaderboardBoard>): void {
		const board = this.boards.get(key);
		if (board) this.boards.set(key, {...board, ...changes});
	}
}

const tableGameLeaderboards = new TableGameLeaderboards();
AccountScopedWork.registerCancellation(() => {
	tableGameLeaderboards.release();
	tableGameLeaderboards.resetSession();
});

export default tableGameLeaderboards;
